import { useState, useEffect, useCallback, useRef } from 'react';

import { useDataProvider, usePermissions } from 'react-admin';
import { USER_GROUP_OWNER_AGENCY } from "../utils/const"

import soundFile from "../utils/sms-alert.mp3";
const audio = new Audio(soundFile);


function useOrderOnAssigning({ handleOpen }) {
  const { permissions } = usePermissions();
  const dataProvider = useDataProvider();
  const [endAt, setEndAt] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const onClear = useCallback(() => {
    setEndAt(null)
    setTotal(0);
    audio.pause();
  }, [])
  useEffect(() => {
    var x;
    if (total > 0) {
      handleOpen()
      audio.play()
      x = setInterval(() => {
        audio.play()
      }, 5000)
    }
    return () => {
      clearInterval(x)
      audio.pause()
    }
  }, [handleOpen, permissions, total])

  // Guards against: (1) an unmounted-component setState (component navigated
  // away while a poll was in flight), (2) two overlapping polls running at
  // once (a slow/stalled request still pending when the next 5s tick, or a
  // resume-from-hidden immediate fetch, fires). Refs (not state) so flipping
  // them never triggers a re-render/race of their own.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  const isFetchingRef = useRef(false);
  // Only the very first-ever fetch should toggle the (initial) `loading`
  // flag - background polling refreshes shouldn't flicker whatever UI is
  // keyed off `loading`. Persists across effect re-runs (unlike a plain
  // variable inside the effect) because it's tied to the component
  // instance, not to one particular effect invocation.
  const hasLoadedOnceRef = useRef(false);

  useEffect(() => {
    // if(permissions === USER_GROUP_OWNER_AGENCY) {
    if (true) {
      async function fetchData() {
        if (isFetchingRef.current) return;
        isFetchingRef.current = true;

        const isInitialLoad = !hasLoadedOnceRef.current;
        if (isInitialLoad && mountedRef.current) {
          setLoading(true);
        }

        try {
          const res = await dataProvider.getList("orderOnAssigning", {
            endAt: endAt,
            status: 'ORDER_STATUS_ON_ASSIGNING',
          });
          const { timestamp: newTimestamp, data = [] } = res || {};
          if (mountedRef.current) {
            setTotal(pre => Number(pre) + data.total);
            setEndAt(newTimestamp);
          }
        } catch (err) {
          // A raw TypeError with no err_code is _fetch's normalized shape for
          // a network-layer failure (fetch() itself throwing - e.g. the tab
          // was backgrounded/asleep and its network stack hadn't resumed
          // yet), not a structured business/auth error. wrapperDataProvider
          // already retried err_code===1015 via auth.checkAuth() before this
          // ever reaches us, so anything else here (a structured err_code, or
          // a plain Error from a non-2xx business response) is real and must
          // not be swallowed - only the raw-network case is transient.
          const isTransientNetworkError = err instanceof TypeError && !err.err_code;
          if (isTransientNetworkError) {
            if (process.env.NODE_ENV === 'development') {
              console.warn('[useOrderOnAssigning] transient network error while polling, keeping last known data - will retry next poll', err);
            }
            // Keep last-known total/endAt as-is; next poll (interval or the
            // next resume-from-hidden) retries on its own.
          } else {
            throw err;
          }
        } finally {
          hasLoadedOnceRef.current = true;
          isFetchingRef.current = false;
          if (isInitialLoad && mountedRef.current) {
            setLoading(false);
          }
        }
      }

      let intervalId;
      const startPolling = () => {
        if (intervalId) return;
        intervalId = setInterval(() => {
          fetchData();
        }, 5 * 1000);
      };
      const stopPolling = () => {
        if (intervalId) {
          clearInterval(intervalId);
          intervalId = undefined;
        }
      };

      // Foreground: poll every 5s as before. Hidden: pause polling entirely
      // instead of pointlessly spending network/battery on a tab nobody's
      // looking at. hidden -> visible: fetch once immediately (data is
      // likely stale after however long it was backgrounded), then resume
      // the 5s cadence.
      const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
          fetchData();
          startPolling();
        } else {
          stopPolling();
        }
      };

      if (document.visibilityState === 'visible') {
        startPolling();
      }
      document.addEventListener('visibilitychange', handleVisibilityChange);

      return () => {
        stopPolling();
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    }
  }, [dataProvider, endAt, permissions])
  return {
    loading,
    total,
    onClear,
  };
}

export default useOrderOnAssigning;
